import { inject, Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import { AutocompleteFunction } from '../../../core/common/entities/autocompleteFunction.entity';
import {
    BaseFieldDefinition,
    BaseNestedFieldDefinition,
    DottedKey,
    FieldDefinitionValue,
    FieldType
} from '../../../core/field-definitions/entities/field-definition.entity';
import { DottedKeyToFlatKey } from '../../../core/field-definitions/helpers/dottedkey-to-flatkey.helper';
import { ShFieldDefinitionService } from '../../../core/field-definitions/services/field-definition.service';
import { ExtendedBaseProfileField } from '../../../core/profiles/entities/extended-profile-field.entity';
import { RecordTypeTranslationService } from '../../../core/record-types/services/record-type-translation.service';
import { ApiFragment } from '../../../core/records/entities/api-fragment.class';
import { RecordId } from '../../../core/records/entities/metadata/metadata.entity';
import { DialogStyle } from '../../../elements/components/dialog/entities/dialog.entity';
import { PERSON_ID_DEFINITION_DOTTED_KEY } from '../../../facial-recognition/constants/field-definition-keys.constants';
import { PersonDataService } from '../../../facial-recognition/services/person.data-service';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { MultiFormSelectOptions } from '../../../form-controls/entities/multi-form-select-options.entity';
import { toFormSelectOption } from '../../../form-controls/helpers/form-select-option-helper';
import { multiFormSelectOptionWrapper } from '../../../form-controls/helpers/multi-form-select-options-wrapper-helper';
import { combineLatestAlwaysEmit } from '../../../helpers/observables/combine-latest-helper';
import { PaginationInfo } from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { ValueType } from '../entities/valueType.entity';

@Injectable({ providedIn: 'root' })
export abstract class BaseAdvancedSearchSandbox<T extends BaseNestedFieldDefinition> {
    public readonly options = { style: DialogStyle.ExtraWide };

    protected fieldDefinitionService: ShFieldDefinitionService = inject(ShFieldDefinitionService);
    protected recordTypeTranslationService: RecordTypeTranslationService = inject(RecordTypeTranslationService);
    protected personsService: PersonDataService = inject(PersonDataService);
    protected translateService: SharedLanguageService = inject(SharedLanguageService);

    /**
     * Given an ExtendedProfileField, return a label (what the user will see).
     * @param profileField
     */
    protected getProfileFieldLabel(profileField: ExtendedBaseProfileField<T>): string {
        return profileField.DisplayLabel;
    }

    /**
     * Converts a profileField into a type (FormSelectOption for the 'field' inputs)
     * @param profileField
     */
    public profileFieldToTypeSelectOption(profileField: ExtendedBaseProfileField<T>): FormSelectOption {
        return { label: profileField.DisplayLabel, value: profileField.FieldDefinition.DottedKey };
    }

    /**
     * Separate method for singular and multiple to allow for custom sorting
     * @param profileFields
     */
    public abstract profileFieldsToTypeSelectOptions(profileFields: ExtendedBaseProfileField<T>[]): FormSelectOption[];

    protected abstract getTranslationOrDefault(key: string, fallback: string): string;

    /**
     * Return the parent field definition
     * @param profileField The field whose parent to return
     */
    protected getParentDefinition(profileField: ExtendedBaseProfileField<T>): BaseFieldDefinition | undefined {
        return profileField.FieldDefinition.Parent;
    }

    /**
     * Optional method to implement in case Thesaurus fields need to be supported
     * @param profileField The field whose parent to return
     * @param searchTerm Search term given by the user
     */
    protected getThesaurusOptions(
        profileField: ExtendedBaseProfileField<T>,
        searchTerm?: string,
        currentValue?: string
    ): Observable<MultiFormSelectOptions> {
        console.error(
            'Encountered ThesaurusUriField',
            profileField.FieldDefinition,
            'but no implementation for method getThesaurusOptions.'
        );
        return of();
    }

    /**
     * Method to implement AI.Faces.Face.Person.RecordId search
     * @param profileField The field whose parent to return
     * @param searchTerm Search term given by the user
     */
    protected getPersonsOptions(searchTerm: string = '', currentValue?: string): Observable<MultiFormSelectOptions> {
        const pagination: PaginationInfo = {
            page: 0,
            resultsPerPage: 100
        };

        const currentPerson$ = currentValue ? this.personsService.getPerson(currentValue as RecordId) : of(null);

        const options$ = this.personsService
            .getPersons(searchTerm, pagination)
            .pipe(map(paginatedResult => paginatedResult.results.map(this.personToFormSelectOption)));

        return options$.pipe(
            switchMap(options => {
                if (currentValue && !options.some(option => option.value === currentValue)) {
                    return currentPerson$.pipe(
                        map(currentPerson => this.addPersonToOptions(options, currentPerson, currentValue))
                    );
                }

                return of(options);
            }),
            map(options => multiFormSelectOptionWrapper(options))
        );
    }

    private personToFormSelectOption(person: ApiFragment): FormSelectOption {
        return toFormSelectOption(person.getRecordId(), person.getTitle());
    }

    private addPersonToOptions(
        options: FormSelectOption[],
        person: ApiFragment | null,
        currentValue: string
    ): FormSelectOption[] {
        if (!person) {
            const unknownPersonOptions: FormSelectOption = toFormSelectOption(
                currentValue,
                this.translateService.translate('facial-recognition.person-not-found')
            );
            return [...options, unknownPersonOptions];
        }

        return [...options, this.personToFormSelectOption(person)];
    }

    /**
     * Optional method to implement in case EnumMap fields need to be supported
     * @param fieldDefinition The enum map field
     * @param searchTerm Search term given by the user
     */
    protected getFieldDefinitionValues(
        fieldDefinition: BaseFieldDefinition,
        searchTerm?: string
    ): Observable<FieldDefinitionValue[]> {
        console.error(
            'Encountered EnumMapField',
            fieldDefinition.DottedKey,
            'but no implementation for method getFieldDefinitionValues.'
        );
        return of();
    }

    /**
     * Determines how to get options for field with ValueType Options if it can't be determined by the FieldType.
     * @param profileField
     * @param searchTerm
     */
    protected getDefaultMultiSelectOptions(
        profileField: ExtendedBaseProfileField<T>,
        searchTerm?: string
    ): Observable<MultiFormSelectOptions> {
        return of({ options: [], isLiveData: true });
    }

    /**
     * Determines how to map a field value to a FormSelectOption.
     * Used primarily to override the value with a translation defined locally in the application.
     * @param profileField
     * @param rawValue
     * @param fallback
     */
    protected fieldValueToFormSelectOption(
        profileField: ExtendedBaseProfileField,
        rawValue: string,
        fallback: string = rawValue
    ): FormSelectOption {
        const flatKey: string = DottedKeyToFlatKey(profileField.FieldDefinition.DottedKey);

        return {
            label: this.getTranslationOrDefault(`overrides.${flatKey}.${rawValue}`, fallback),
            value: rawValue
        };
    }

    /**
     * Determines the value type of a field (used to determine the necessary kind of input)
     * @param profileField
     */
    public getValueType(profileField: ExtendedBaseProfileField<T>): ValueType {
        switch (profileField.FieldDefinition.Type) {
            case FieldType.EnumField:
            case FieldType.BooleanField:
            case FieldType.ThesaurusUriField:
                return ValueType.Option;
            case FieldType.LongField:
                return ValueType.Number;
            case FieldType.DateField:
                return ValueType.Date;
            case FieldType.TextField:
                return ValueType.LongText;

            default: {
                const parentDefinition = this.getParentDefinition(profileField);

                if (profileField.DottedKey === PERSON_ID_DEFINITION_DOTTED_KEY) {
                    return ValueType.Option;
                }
                if (parentDefinition?.Type === FieldType.EnumMapField) {
                    return ValueType.Option;
                }
                return ValueType.Text;
            }
        }
    }

    /**
     * Sandbox layer to get profile fields from service
     */
    public abstract getProfileFieldsStream(tag?: string): Observable<ExtendedBaseProfileField<T>[]>;

    /**
     * Get specific particular fielddefinitions
     * Used to represent fields in AdvancedSearch that are not present in the profile
     * This can occur when a saved search is used containing field that has since been removed from the profile, quick-searching or url manipulation
     *
     * @param dottedKeys
     * @returns
     */
    public getFieldDefinitions(dottedKeys: DottedKey[]): Observable<BaseNestedFieldDefinition[]> {
        return this.fieldDefinitionService.getByDottedKeys(dottedKeys);
    }

    // Necessary to support legacy saved searches
    public getFieldDefinitionsByFlatKeys(flatKeys: string[]): Observable<BaseNestedFieldDefinition[]> {
        const fieldDefinitions: Observable<BaseNestedFieldDefinition | undefined>[] = flatKeys.map(key =>
            this.fieldDefinitionService.fetchByFlatKey(key)
        );

        return combineLatestAlwaysEmit([...fieldDefinitions]).pipe(
            map(fieldDefinitions =>
                fieldDefinitions.filter((def): def is BaseNestedFieldDefinition => def !== undefined)
            )
        );
    }

    /**
     * Given a profile field return select options for the 'value' field
     * @param profileField
     * @param searchTerm
     */
    public mapProfileFieldsToValueMultiSelectOptions(
        profileField: ExtendedBaseProfileField<T>,
        searchTerm?: string,
        currentValue?: string
    ): Observable<MultiFormSelectOptions> {
        switch (profileField.FieldDefinition.Type) {
            case FieldType.EnumField: {
                return of({
                    options: this.getPossibleValuesAsOptions(profileField),
                    isLiveData: false
                });
            }

            case FieldType.BooleanField: {
                return of({
                    options: this.getBooleanOptions(profileField),
                    isLiveData: false
                });
            }

            case FieldType.ThesaurusUriField: {
                return this.getThesaurusOptions(profileField, searchTerm, currentValue);
            }

            default: {
                const parentFieldDefinition = this.getParentDefinition(profileField);

                if (profileField.DottedKey === PERSON_ID_DEFINITION_DOTTED_KEY) {
                    return this.getPersonsOptions(searchTerm, currentValue);
                }
                if (parentFieldDefinition?.Type === FieldType.EnumMapField) {
                    return this.getEnumMapFieldOptions(parentFieldDefinition, searchTerm);
                }
                return this.getDefaultMultiSelectOptions(profileField, searchTerm);
            }
        }
    }

    private getPossibleValuesAsOptions(profileField: ExtendedBaseProfileField<T>): FormSelectOption[] {
        if (!profileField.FieldDefinition.PossibleValues) {
            return [];
        }

        if (profileField.DottedKey === 'Administrative.RecordType') {
            return this.recordTypeTranslationService.getActiveRecordTypeOptions();
        }

        return profileField.FieldDefinition.PossibleValues.map(value =>
            this.fieldValueToFormSelectOption(profileField, value)
        );
    }

    private getBooleanOptions(profileField: ExtendedBaseProfileField<T>): FormSelectOption[] {
        return ['true', 'false'].map(rawValue =>
            this.fieldValueToFormSelectOption(
                profileField,
                rawValue,
                this.getTranslationOrDefault(`overrides.BooleanField.${rawValue}`, rawValue)
            )
        );
    }

    private getEnumMapFieldOptions(
        fieldDefinition: BaseFieldDefinition,
        searchTerm?: string
    ): Observable<MultiFormSelectOptions> {
        return this.getFieldDefinitionValues(fieldDefinition, searchTerm).pipe(
            map(values => ({
                options: values.map(value => {
                    return { label: value.Value.Label, value: value.Value.Label };
                }),
                isLiveData: true
            }))
        );
    }

    /**
     * Given the dottedKey of a field definition, return a function that, given a query makes an
     * autocomplete API call on given field definition.
     * @param dottedKey
     */
    public abstract getAutocompleteFct(dottedKey: string): AutocompleteFunction | undefined;
}
